/**
 * SubAIVA Worker — Entry Point
 *
 * Routes incoming requests to the correct SubAIVA Durable Object instance.
 * Each customer gets their own DO, identified by customer ID.
 *
 * Authentication: Bearer token on all /api/* routes.
 * WebSocket: /ws/:customerId for real-time chat.
 * HTTP: /api/:customerId/* for REST-style operations.
 */
import { Hono } from "hono";
import { bearerAuth } from "hono/bearer-auth";
import type { Env } from "./types";
import { TIER_CONFIGS } from "./types";

// Re-export the Durable Object class so wrangler can find it
export { SubAIVA } from "./subaiva";

type HonoEnv = { Bindings: Env };

const app = new Hono<HonoEnv>();

// ─── PUBLIC ROUTES ────────────────────────────────────────

app.get("/health", (c) => {
  return c.json({
    status: "ok",
    service: "subaiva",
    timestamp: new Date().toISOString(),
  });
});

app.get("/tiers", (c) => {
  return c.json({ tiers: TIER_CONFIGS });
});

// ─── AUTHENTICATED ROUTES ─────────────────────────────────

// Protect all /api/* and /ws/* routes
app.use("/api/*", (c, next) => {
  const auth = bearerAuth({ token: c.env.API_TOKEN });
  return auth(c, next);
});

// Helper: get DO stub for a customer
function getSubAIVA(env: Env, customerId: string) {
  const id = env.SUBAIVA.idFromName(customerId);
  return env.SUBAIVA.get(id);
}

// ─── PROVISIONING ─────────────────────────────────────────

/**
 * POST /api/provision
 * Create a new SubAIVA for a customer.
 * Body: { customerId, tier, systemPrompt? }
 */
app.post("/api/provision", async (c) => {
  const body = await c.req.json<{
    customerId: string;
    tier: number;
    systemPrompt?: string;
  }>();

  if (!body.customerId || !body.tier) {
    return c.json({ error: "customerId and tier are required" }, 400);
  }

  if (!TIER_CONFIGS[body.tier]) {
    return c.json({ error: `Invalid tier: ${body.tier}. Valid: 1, 2, 3` }, 400);
  }

  // Store entitlement in KV for fast edge lookups (if KV available)
  if (c.env.ENTITLEMENTS) {
    await c.env.ENTITLEMENTS.put(
      `customer:${body.customerId}`,
      JSON.stringify({
        tier: body.tier,
        provisionedAt: Date.now(),
        active: true,
      })
    );
  }

  const stub = getSubAIVA(c.env, body.customerId);
  const state = await stub.provision(
    body.customerId,
    body.tier,
    body.systemPrompt
  );

  return c.json({ success: true, state });
});

// ─── CHAT (HTTP) ──────────────────────────────────────────

/**
 * POST /api/:customerId/chat
 * Send a message and get a response.
 * Body: { message: string }
 */
app.post("/api/:customerId/chat", async (c) => {
  const customerId = c.req.param("customerId");
  const body = await c.req.json<{ message: string }>();

  if (!body.message) {
    return c.json({ error: "message is required" }, 400);
  }

  const stub = getSubAIVA(c.env, customerId);
  const response = await stub.chat(body.message);

  return c.json({ response });
});

// ─── STATUS ───────────────────────────────────────────────

/**
 * GET /api/:customerId/status
 * Get SubAIVA status including memory/message counts.
 */
app.get("/api/:customerId/status", async (c) => {
  const customerId = c.req.param("customerId");
  const stub = getSubAIVA(c.env, customerId);
  const status = await stub.getStatus();
  return c.json(status);
});

// ─── TIER MANAGEMENT ──────────────────────────────────────

/**
 * PUT /api/:customerId/tier
 * Update subscription tier.
 * Body: { tier: number }
 */
app.put("/api/:customerId/tier", async (c) => {
  const customerId = c.req.param("customerId");
  const body = await c.req.json<{ tier: number }>();

  if (!TIER_CONFIGS[body.tier]) {
    return c.json({ error: `Invalid tier: ${body.tier}` }, 400);
  }

  // Update KV entitlement (if KV available)
  if (c.env.ENTITLEMENTS) {
    await c.env.ENTITLEMENTS.put(
      `customer:${customerId}`,
      JSON.stringify({
        tier: body.tier,
        updatedAt: Date.now(),
        active: true,
      })
    );
  }

  const stub = getSubAIVA(c.env, customerId);
  const tierConfig = await stub.updateTier(body.tier);

  return c.json({ success: true, tier: tierConfig });
});

// ─── MEMORY ───────────────────────────────────────────────

/**
 * POST /api/:customerId/memory
 * Store a memory.
 * Body: { content, category?, importance? }
 */
app.post("/api/:customerId/memory", async (c) => {
  const customerId = c.req.param("customerId");
  const body = await c.req.json<{
    content: string;
    category?: string;
    importance?: number;
  }>();

  if (!body.content) {
    return c.json({ error: "content is required" }, 400);
  }

  const stub = getSubAIVA(c.env, customerId);
  const memory = await stub.storeMemory(
    body.content,
    body.category,
    body.importance
  );

  return c.json({ memory });
});

/**
 * POST /api/:customerId/memory/search
 * Search memories semantically.
 * Body: { query, topK? }
 */
app.post("/api/:customerId/memory/search", async (c) => {
  const customerId = c.req.param("customerId");
  const body = await c.req.json<{ query: string; topK?: number }>();

  if (!body.query) {
    return c.json({ error: "query is required" }, 400);
  }

  const stub = getSubAIVA(c.env, customerId);
  const memories = await stub.searchMemories(body.query, body.topK);

  return c.json({ memories });
});

// ─── WEBSOCKET UPGRADE ───────────────────────────────────

/**
 * WebSocket connections bypass Hono — handled at the Worker level.
 * /ws/:customerId — connects to the customer's SubAIVA DO via WebSocket.
 */

// Default export — the Worker entry point
export default {
  async fetch(
    request: Request,
    env: Env,
    ctx: ExecutionContext
  ): Promise<Response> {
    const url = new URL(request.url);

    // WebSocket route — bypass Hono, route directly to DO
    if (
      url.pathname.startsWith("/ws/") &&
      request.headers.get("Upgrade") === "websocket"
    ) {
      // Authenticate WebSocket connections via query param
      const token = url.searchParams.get("token");
      if (token !== env.API_TOKEN) {
        return new Response("Unauthorized", { status: 401 });
      }

      const customerId = url.pathname.split("/ws/")[1];
      if (!customerId) {
        return new Response("Customer ID required", { status: 400 });
      }

      const id = env.SUBAIVA.idFromName(customerId);
      const stub = env.SUBAIVA.get(id);
      return stub.fetch(request);
    }

    // All other routes go through Hono
    return app.fetch(request, env, ctx);
  },
} satisfies ExportedHandler<Env>;
